import React, {
  useEffect,
  ReactNode,
  useReducer,
  useRef,
  useMemo,
  Reducer,
  ReducerAction,
  useCallback,
  useState,
} from "react"

import { after } from "lodash"

import { setNavbar } from "@docs/common"

import { ReactComponent as BackIcon } from "./ic_back.svg"

import "./style.scss"

const UPDATE = "UPDATE_NAV_OPTIONS"

let singleton: React.Dispatch<NavigationReducerAction>

const sall = <T extends unknown>(fn: () => T) => fn()

type NavigationReducerAction = ReducerAction<
  Reducer<NavigationBarState, NavigationBarAction>
>

type NavigationBarAction = {
  type: typeof UPDATE
  payload: Partial<NavigationBarState>
}

type NavigationBarProps = {
  scope?: "faq" | "tutorial"
}

type NavigationBarState = {
  title?: string | null
  hidden?: boolean
  action?: ReactNode
  extra?: ReactNode
  className?: string
  /** 默认返回行为为: 调用 history.back, 没成功则调用 app://back 协议 */
  onBack?: () => void
}

const DEFAULT_BACK = () => {
  // 返回上一个 history 地址, 如果要控制 App 返回, 单独设置 onBack 事件
  window.history.go(-1)
}

const initialState = {
  title: "",
  onBack: DEFAULT_BACK,
}

function reducer(state: NavigationBarState, action: NavigationBarAction) {
  switch (action.type) {
    case UPDATE:
      return {
        // 设置初始状态
        action: null,
        extra: null,
        title: "",
        hidden: false,
        onBack: DEFAULT_BACK,
        ...action.payload,
      }
    default:
      return state
  }
}

function useAfter<T>(n: number, fn: T): T {
  const fnRef = useRef<T>(fn)
  fnRef.current = fn

  const callback = useMemo(
    () => after(n, fnRef.current as unknown as () => unknown),
    []
  )

  return callback as unknown as T
}

function NavigationBar(props: NavigationBarProps) {
  const [state, dispatch] = useReducer<typeof reducer>(reducer, initialState)
  const { title, hidden = true, action, extra, onBack, className } = state
  const [backVisible, setBackVisible] = useState(true)

  const updateNavbar = useAfter(2, (title: string, hideNavbar: boolean) => {
    setNavbar({
      navTitle: (typeof title === "string" && title) || "",
      hideNavbar,
      backOperation: 1,
    })
  })

  if (typeof singleton === "undefined") {
    singleton = dispatch
  }

  const handleBackClick = useCallback(() => {
    onBack?.()
  }, [onBack])

  useEffect(() => {
    document.body.classList.toggle("with-navigation", !hidden)
  }, [hidden])

  useEffect(() => {
    sall(async () => {
      try {
        // 隐藏 App 的导航条, 使用自定义的
        const hideNavbar = !hidden
        updateNavbar(title || "", hideNavbar)
      } catch {
        if (process.env.NODE_ENV === "production") {
          // singleton?.({
          //   type: UPDATE,
          //   payload: {
          //     hidden: true,
          //   },
          // })
        }
      }
    })
  }, [hidden, title])

  useEffect(() => {
    // 需要延迟判断是否隐藏返回按钮
    setTimeout(() => {
      // 如果是在浏览器环境下，没有上一页则隐藏返回按钮
      if (!(window as any).__IS_APP__ && window.history.length <= 1) {
        setBackVisible(false)
      }
    }, 300)
  }, [])

  if (hidden) {
    return null
  }
  return (
    <div className={["navigation-bar", className].join(" ")}>
      {!!action && action}
      {!action && (
        <>
          {backVisible && (
            <i onClick={handleBackClick}>
              <BackIcon />
            </i>
          )}
          <span>{title}</span>
          {!!extra && <div className="navigation-bar-extra">{extra}</div>}
        </>
      )}
    </div>
  )
}

export function setNavigationBar(options: NavigationBarState) {
  singleton?.({ type: UPDATE, payload: options })
}

export default NavigationBar
